// Helper function to emit BOLD cart loaded event
function emitBoldCartLoaded() {
  if (
    typeof BOLD === 'object' &&
    BOLD.common &&
    BOLD.common.eventEmitter &&
    typeof BOLD.common.eventEmitter.emit === 'function'
  ) {
    BOLD.common.eventEmitter.emit('BOLD_COMMON_cart_loaded');
  }
}

// Check if we're on the cart page
function isCartPage() {
  const path = window.location.pathname;
  return path === '/cart' || path === '/cart/' || path.includes('/cart');
}

// Reload cart page content after quantity change (exposed globally for add-to-cart from upsell etc.)
async function reloadCartPageContent() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_', Date.now()); // Cache bust

    const response = await fetch(url.toString(), { cache: 'no-store' });
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Reload cart items section
    const cartItems = document.querySelector('#main-cart-items .js-contents');
    const newCartItems = doc.querySelector('#main-cart-items .js-contents');
    if (cartItems && newCartItems) {
      cartItems.innerHTML = newCartItems.innerHTML;
    }

    // Reload cart footer/payment section
    const cartFooter = document.querySelector('#main-cart-footer .desky-cart-payment-slider');
    const newCartFooter = doc.querySelector('#main-cart-footer .desky-cart-payment-slider');
    if (cartFooter && newCartFooter) {
      cartFooter.innerHTML = newCartFooter.innerHTML;
    }

    // Reload cart icon bubble
    const cartBubble = document.querySelector('#cart-icon-bubble');
    const newCartBubble = doc.querySelector('#cart-icon-bubble');
    if (cartBubble && newCartBubble) {
      cartBubble.innerHTML = newCartBubble.innerHTML;
    }

    // Let BOLD process the new content
    emitBoldCartLoaded();
  } catch (error) {
    console.error('Failed to reload cart page:', error);
  }
}

// Expose for add-to-cart from .desky-custom-upsell and other cart page updates
window.reloadCartPageContent = reloadCartPageContent;
window.isCartPage = isCartPage;

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      this.closest('cart-items').updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0
    );

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
    ];

    // Include cart payment totals (subtotal + savings) so they update on quantity change
    const paymentTotals = document.getElementById('cart-payment-totals');
    if (paymentTotals) {
      sections.push({
        id: 'cart-payment-totals',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '#cart-payment-totals',
      });
    }

    return sections;
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then(async (state) => {
        const parsedState = JSON.parse(state);
        this.classList.toggle('is-empty', parsedState.item_count === 0);

        if (isCartPage()) {
          // On cart page, reload content from server to get correct BOLD prices
          await reloadCartPageContent();
          this.updateLiveRegions(line, parsedState.item_count);
          this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
          this.disableLoading();
        } else {
          // On other pages (cart drawer), use section rendering
          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });

          this.updateLiveRegions(line, parsedState.item_count);
          document.getElementById(`CartItem-${line}`)?.querySelector(`[name="${name}"]`)?.focus();
          this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
          this.disableLoading();

          // Emit BOLD event for cart drawer
          emitBoldCartLoaded();
        }
      })
      .catch((error) => {
        console.error('Cart update failed:', error);
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        this.disableLoading();
      });
  }

  updateLiveRegions(line, itemCount) {
    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');
    cartStatus.setAttribute('aria-hidden', false);

    // This timeout is intentional for accessibility - gives screen readers time to announce the change
    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    document.getElementById('main-cart-items').classList.add('cart__items--disabled');
    this.querySelector(`#CartItem-${line} .loading-overlay`)?.classList.remove('hidden');
    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    document.getElementById('main-cart-items').classList.remove('cart__items--disabled');
  }
}

customElements.define('cart-items', CartItems);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      'change',
      debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }, 100)
    );
  }
}

customElements.define('cart-note', CartNote);
